import { env } from '@fim/config';
import { createLogger } from '@fim/logger';
import { retry, ValidationError, type ValidationIssue } from '@fim/utils';
import type { ZodError } from 'zod';

import { AiProviderError } from './internal/http.js';
import { anthropicProvider } from './providers/anthropic.js';
import { deepseekProvider } from './providers/deepseek.js';
import { openaiProvider } from './providers/openai.js';
import type { AiProvider, ProviderOptions } from './provider.js';
import {
  chatRequestSchema,
  embedRequestSchema,
  type AiModel,
  type AiProviderName,
  type ChatChunk,
  type ChatRequest,
  type ChatResult,
  type EmbedRequest,
  type EmbedResponse,
} from './types.js';

const logger = createLogger('ai-sdk');

/** Options for {@link createAiClient}. */
export interface AiClientOptions extends ProviderOptions {
  /**
   * Force a specific provider. When omitted, the provider is chosen from
   * `env.AI_PROVIDER`, defaulting to `deepseek` (DeepSeek-only system).
   */
  readonly provider?: AiProviderName;
  /** Maximum retry attempts for transient failures. Defaults to 3. */
  readonly maxRetries?: number;
  /** Base backoff in milliseconds for retries. Defaults to 500. */
  readonly retryBaseMs?: number;
}

/**
 * The public client surface. Adds validation, retry, and convenience helpers
 * on top of the selected {@link AiProvider}.
 */
export interface AiClient {
  readonly provider: AiProvider;
  readonly providerName: AiProviderName;

  /** Run a chat completion, returning the assistant text. */
  chat(request: ChatRequest): Promise<string>;

  /** Run a chat completion, returning the full result with usage/finish data. */
  chatVerbose(request: ChatRequest): Promise<ChatResult>;

  /** Stream a chat completion as {@link ChatChunk}s. */
  stream(request: ChatRequest): AsyncIterable<ChatChunk>;

  /** Embed one or more inputs into dense vectors. */
  embed(request: EmbedRequest): Promise<EmbedResponse>;

  /** List the models the active provider can address. */
  listModels(): readonly AiModel[];
}

const PROVIDER_FACTORIES: Record<
  AiProviderName,
  (options?: ProviderOptions) => AiProvider
> = {
  anthropic: anthropicProvider,
  openai: openaiProvider,
  deepseek: deepseekProvider,
};

/**
 * Create an {@link AiClient}, selecting the backend from `opts.provider` or
 * `env.AI_PROVIDER` (default `deepseek`).
 *
 * Non-streaming calls (`chat`, `chatVerbose`, `embed`) are wrapped in
 * exponential-backoff retry via `@fim/utils.retry`, retrying only on transient
 * provider errors (HTTP 408/429/5xx). Streaming is intentionally not retried —
 * a half-consumed stream cannot be safely replayed; callers restart explicitly.
 */
export function createAiClient(opts: AiClientOptions = {}): AiClient {
  const providerName = resolveProviderName(opts.provider);
  const factory = PROVIDER_FACTORIES[providerName];
  const provider = factory(toProviderOptions(opts));

  const maxRetries = opts.maxRetries ?? 3;
  const retryBaseMs = opts.retryBaseMs ?? 500;

  const withRetry = <T>(label: string, fn: () => Promise<T>): Promise<T> =>
    retry(fn, {
      retries: maxRetries,
      delay: retryBaseMs,
      backoff: 'exponential',
      shouldRetry: isRetryable,
      onRetry: (error, attempt) => {
        logger.warn(
          { provider: providerName, op: label, attempt, err: describe(error) },
          'retrying AI request',
        );
      },
    });

  return {
    provider,
    providerName,

    async chat(request: ChatRequest): Promise<string> {
      const validated = validateChat(request);
      return withRetry('chat', () => provider.chat(validated));
    },

    async chatVerbose(request: ChatRequest): Promise<ChatResult> {
      const validated = validateChat(request);
      return withRetry('chatVerbose', () => provider.chatVerbose(validated));
    },

    stream(request: ChatRequest): AsyncIterable<ChatChunk> {
      const validated = validateChat({ ...request, stream: true });
      return provider.stream(validated);
    },

    async embed(request: EmbedRequest): Promise<EmbedResponse> {
      const validated = validateEmbed(request);
      return withRetry('embed', () => provider.embed(validated));
    },

    listModels(): readonly AiModel[] {
      return provider.models;
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function resolveProviderName(explicit?: AiProviderName): AiProviderName {
  // Read AI_PROVIDER defensively: the value is validated by `@fim/config`, but
  // we tolerate config builds that predate the key by treating it as optional.
  const fromEnv = (env as Record<string, unknown>).AI_PROVIDER;
  const candidate =
    explicit ?? (typeof fromEnv === 'string' ? (fromEnv as AiProviderName) : undefined);
  if (candidate && candidate in PROVIDER_FACTORIES) {
    return candidate;
  }
  if (candidate) {
    logger.warn(
      { requested: candidate },
      'unknown AI_PROVIDER, falling back to deepseek',
    );
  }
  // DeepSeek-only system: DeepSeek is the default provider.
  return 'deepseek';
}

function toProviderOptions(opts: AiClientOptions): ProviderOptions {
  const { apiKey, baseUrl, defaultModel, defaultEmbeddingModel, timeoutMs, fetch, headers } =
    opts;
  return { apiKey, baseUrl, defaultModel, defaultEmbeddingModel, timeoutMs, fetch, headers };
}

function validateChat(request: ChatRequest): ChatRequest {
  const result = chatRequestSchema.safeParse(request);
  if (!result.success) {
    throw new ValidationError('Invalid chat request', toIssues(result.error));
  }
  // Re-attach non-serialisable fields the schema strips (signal).
  return { ...request, ...result.data };
}

function validateEmbed(request: EmbedRequest): EmbedRequest {
  const result = embedRequestSchema.safeParse(request);
  if (!result.success) {
    throw new ValidationError('Invalid embed request', toIssues(result.error));
  }
  return { ...request, ...result.data };
}

/** Flatten a {@link ZodError} into the platform's {@link ValidationIssue} shape. */
function toIssues(error: ZodError): ValidationIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.join('.'),
    message: issue.message,
    code: issue.code,
  }));
}

function isRetryable(error: unknown): boolean {
  if (error instanceof AiProviderError) return error.retryable;
  // Network-level failures (DNS, reset, abort-on-timeout) are worth one more try.
  if (error instanceof Error) {
    return (
      error.name === 'AbortError' ||
      error.name === 'TimeoutError' ||
      /network|fetch failed|ECONN|ETIMEDOUT|EAI_AGAIN/i.test(error.message)
    );
  }
  return false;
}

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}
